// @flow
import * as React from "react"
import ReactDOM from "react-dom"

import { get } from "common/utils/get"
import { oneTimeAnimate } from "common/services/utils/animations"

export type SpFieldFunc = {
  setDirty: () => void,
  shouldShowState: () => boolean,
  hasError: () => boolean,
  renderError: () => React.Node | string,
  renderLabel: () => ?React.Node,
  getClasses: () => string,
  getValidationClasses: () => string,
  // eslint-disable-next-line flowtype/no-weak-types
  createValueLink: (props: Object) => Object,
}

const spFieldWrapper = (WrappedComponent: React.ComponentType<*>) =>
  class extends React.Component<any, any> {
    static defaultProps = {
      inputStyle: "twoLines", // twoLines, oneLine
    }

    // eslint-disable-next-line flowtype/no-weak-types
    constructor(props: Object) {
      super(props)

      this.spFieldFunc = {
        setDirty: this.setDirty,
        shouldShowState: this.shouldShowState,
        hasError: this.hasError,
        renderError: this.renderError,
        renderLabel: this.renderLabel,
        getClasses: this.getClasses,
        getValidationClasses: this.getValidationClasses,
        createValueLink: this.createValueLink,
      }
    }

    state = {
      dirty: false,
    }

    // $FlowFixMe - flow is stupid, this is really necessary!
    spFieldFunc = null

    setDirty = () => {
      this.setState({
        dirty: true,
      })
    }

    getClasses = (): string => ` _${this.props.inputStyle} ${this.getValidationClasses()} `

    getValidationClasses = (): string => {
      if (this.shouldShowState() && this.hasError()) {
        return " _error "
      }
      if (this.shouldShowState() && !this.hasError()) {
        return " _ok "
      }
      // Validation for select field  from redux form
      if (this.props.touched && this.props.error) {
        return " _error"
      }
      if ((this.props.invalid && this.props.touched) || this.props.contextError) {
        return " _error"
      }
      if (this.props.touched && this.props.valid) {
        return " _ok"
      }

      return ""
    }

    // eslint-disable-next-line flowtype/no-weak-types
    createValueLink = (props: Object): Object => {
      const { disableShaking, isMobile, normalize, onChange, valueLink, maxLength } = props

      const value = valueLink ? valueLink.value : props.value

      // $FlowFixMe
      const handleChange = val => {
        let normalizedValue

        if (Array.isArray(normalize)) {
          normalizedValue = val

          normalize.forEach(normalizer => {
            normalizedValue = normalizer
              ? normalizer(normalizedValue, props.maxLength)
              : normalizedValue
          })
        } else {
          normalizedValue = normalize ? normalize(val) : val
        }

        if (
          this.refs.field &&
          normalize &&
          val !== normalizedValue &&
          !isMobile &&
          !disableShaking
        ) {
          oneTimeAnimate(ReactDOM.findDOMNode(this.refs.field), "spShake")
        }

        if (valueLink) {
          return valueLink.requestChange(normalizedValue)
        }

        return onChange(normalizedValue, maxLength)
      }

      const safeValue = value || ""

      return {
        value: props.format && props.value && !props.active ? props.format(safeValue) : safeValue,
        onChange: e => handleChange(e.target.value),
      }
    }

    hasError = (): boolean =>
      Boolean(this.props.valueLink && get(["valueLink", "errors", "length"], this.props))

    shouldShowState = (): boolean =>
      Boolean(this.state.dirty || get(["valueLink", "forceShowState"], this.props))

    renderError = (): React.Node | string => {
      if (this.hasError() && this.shouldShowState()) {
        return (
          <div className="spField-error">
            {
              this.props.valueLink.errors.map(message => (
                <span className="spField-note spTypo-note" key={message}>
                  {message}
                </span>
              ))[0]
            }
          </div>
        )
      }
      return ""
    }

    renderLabel = (): ?React.Node => {
      if (this.props.label) {
        return (
          <div className="spField-label">
            {this.props.label ? this.props.label : ""}
            {this.props.hintText && (
              <span className="spField-hintText"> - {this.props.hintText}</span>
            )}
          </div>
        )
      }

      return null
    }

    render = () => (
      <WrappedComponent dirty={this.state.dirty} spFieldFunc={this.spFieldFunc} {...this.props} />
    )
  }

export default spFieldWrapper
